import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useCallback, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApolloQuery } from "../../../../../hooks";
import { EyeCloseIcon, EyeOpenIcon } from "../../../../../@lib/icons";

const CREATE_USER = gql`
  mutation UserCreate($input: UserCreateInput!) {
    userCreate(input: $input) {
      clientMutationId
      user {
        companyName
        email
        firstName
        id
        isConfirmed
        isLocked
        lang
        lastName
        profile {
          id
          name
        }
      }
    }
  }
`;

const GET_USER = gql`
  query User($id: ID!) {
    user(id: $id) {
      companyName
      email
      firstName
      id
      isConfirmed
      isLocked
      lang
      lastName
      profile {
        id
        name
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation UserUpdateByAdmin(
    $id: ID!
    $email: String!
    $firstName: String!
    $lastName: String!
    $profileId: String!
    $password: String
    $passwordConfirmation: String
    $companyName: String!
  ) {
    userUpdateByAdmin(
      input: {
        id: $id
        userInput: {
          companyName: $companyName
          email: $email
          firstName: $firstName
          lastName: $lastName
          profileId: $profileId
          password: $password
          passwordConfirmation: $passwordConfirmation
        }
      }
    ) {
      clientMutationId
      user {
        id
        firstName
        lastName
        email
        companyName
        createdAt
        updatedAt
        isConfirmed
        isLocked
        lang
        profile {
          id
          name
        }
      }
    }
  }
`;

const GET_PROFILES = gql`
  query GetProfiles {
    profiles {
      name
      id
    }
  }
`;

function UsersAction({ isUpdate = false }: any) {
  const [showPassword, setShowPassword] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState(false);

  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const user_id = searchParams.get("id");

  const togglePasswordVisibility = () => {
    setShowPassword((prev: any) => !prev);
  };

  const toggleConfirmNewPasswordVisibility = () => {
    setConfirmNewPassword((prev) => !prev);
  };

  const { data: profilesData } = useApolloQuery({
    queryDocument: GET_PROFILES,
    isPrivateCall: true,
  });

  const { data: userData } = useQuery(GET_USER, {
    variables: { id: user_id },
    skip: !user_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createUser, { loading, error }] = useMutation(CREATE_USER, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!userData?.user) return undefined;

    const { firstName, lastName, email, companyName, profile, password } =
      userData?.user;

    return {
      first_name: firstName,
      last_name: lastName,
      password: password,
      email: email,
      password_confirmation: "",
      company_name: companyName,
      profileId: profile?.id,
    };
  }, [userData]);

  const form = useForm<any>({
    defaultValues: {
      first_name: "",
      last_name: "",
      password: "",
      email: "",
      password_confirmation: "",
      company_name: "",
      profileId: null,
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateUser({
          variables: {
            id: Number(user_id),
            profileId: data?.profileId,
            firstName: data?.first_name,
            lastName: data?.last_name,
            email: data?.email,
            ...(data?.password ? { password: data?.password } : {}),
            ...(data?.password_confirmation
              ? { passwordConfirmation: data?.password_confirmation }
              : {}),
            companyName: data?.company_name,
          },
        });
        if (response.data) {
          notify.success(`User Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/users");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }
    const response = await createUser({
      variables: {
        input: {
          userInput: {
            email: data?.email,
            firstName: data?.first_name,
            lastName: data?.last_name,
            password: data?.password,
            passwordConfirmation: data?.password_confirmation,
            profileId: data?.profileId,
            companyName: data?.company_name,
          },
        },
      },
    });
    try {
      if (actionType === "save") {
        if (response.data) {
          notify.success(`User Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/users");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`User Created successfuly!`);
          form?.reset();
        }
      }
    } catch (err) {}
  }, []);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
          pt={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="first_name"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("support.First Name")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="last_name"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("support.Last Name")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="password"
              control={form.control}
              rules={{
                ...(!isUpdate ? { required: "Required!" } : {}),
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters long",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  sx={{ margin: "0px" }}
                  fullWidth
                  required
                  error={!!error}
                  margin="normal"
                >
                  <FormLabel htmlFor="password">
                    {translator("generic.Password")}
                  </FormLabel>
                  <OutlinedInput
                    {...field}
                    id="password"
                    // type="password"
                    type={showPassword ? `text` : "password"}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="email"
              rules={{
                required: "Required!",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: "Email is in wrong format",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("support.Email")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="password_confirmation"
              control={form.control}
              rules={{
                ...(!isUpdate ? { required: "Required!" } : {}),
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters long",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  sx={{ margin: "0px" }}
                  fullWidth
                  required
                  error={!!error}
                  margin="normal"
                >
                  <FormLabel htmlFor="password_confirmation">
                    {translator("generic.Password")} Confirmation
                  </FormLabel>
                  <OutlinedInput
                    {...field}
                    id="password_confirmation"
                    // type="password"
                    type={confirmNewPassword ? `text` : "password"}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleConfirmNewPasswordVisibility}
                          edge="end"
                        >
                          {confirmNewPassword ? (
                            <EyeOpenIcon />
                          ) : (
                            <EyeCloseIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="company_name"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Company Name")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="profileId"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("generic.Profile")}</FormLabel>
                  <Select {...field} displayEmpty>
                    {profilesData?.profiles?.map((op: any, i: any) => (
                      <MenuItem key={`${op.value}-${i}`} value={op.id}>
                        {op.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/users"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </FormProvider>
  );
}

export { UsersAction };
